import React, { useState, useRef, useEffect, useCallback } from "react";
import { apiGet, apiPost, apiPatch, apiDelete } from "../../services/api";
import { safeDate } from "../../utils/dateUtils";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
export interface PatientHistoryProps {
  selectedPatient: { patient_id: string; name?: string;[k: string]: unknown } | null;
  onDeleteSuccess?: () => void;
  refreshKey?: number;
  onVisitAdded?: () => void;
}

interface VisitItem {
  visit_id: number;
  visit_date?: string;
  visit_type?: string;
  remarks?: string;
  complaint?: string;
  status?: string;
}

interface PatientDetails {
  patient_id: string;
  name?: string;
  age?: string | number;
  gender?: string;
  area?: string;
  reference_source?: string;
  medical_alerts?: string;
  [k: string]: unknown;
}

const PatientHistory = ({ selectedPatient, onDeleteSuccess, refreshKey, onVisitAdded }: PatientHistoryProps) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [details, setDetails] = useState<PatientDetails | null>(null);
  const [visits, setVisits] = useState<VisitItem[]>([]);
  const [followups, setFollowups] = useState<any[]>([]);
  const [historyTab, setHistoryTab] = useState<'visits' | 'followups'>('visits');
  const [isEditing, setIsEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState<Partial<PatientDetails>>({});

  const activeRequestRef = useRef(0);

  const loadPatientData = useCallback(async (id: string) => {
    const requestId = ++activeRequestRef.current;
    setLoading(true);
    setConfirmArchive(false);
    setIsEditing(false);

    try {
      const [patientRes, visitsRes, followupsRes] = await Promise.all([
        apiGet(`/api/patients/${id}`),
        apiGet(`/api/visits/patient/${id}`),
        apiGet(`/api/followups?patient_id=${id}`),
      ]);

      if (requestId !== activeRequestRef.current) return;

      setDetails((patientRes as PatientDetails) || null);
      setVisits(Array.isArray(visitsRes) ? (visitsRes as VisitItem[]) : []);
      setFollowups(Array.isArray(followupsRes) ? followupsRes : []);
    } catch (err) {
      if (requestId !== activeRequestRef.current) return;
      console.error("❌ PatientHistory load error:", err);
      setDetails(null);
      setVisits([]);
      setFollowups([]);
    } finally {
      if (requestId === activeRequestRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedPatient?.patient_id) {
      loadPatientData(selectedPatient.patient_id);
    } else {
      setDetails(null);
      setVisits([]);
      setFollowups([]);
    }
  }, [selectedPatient?.patient_id, refreshKey, loadPatientData]);

  const handleArchive = async () => {
    if (!details?.patient_id) return;
    try {
      await apiDelete(`/api/patients/${encodeURIComponent(details.patient_id)}`);
      showToast({ message: "Patient archived successfully", type: "success" });
      onDeleteSuccess?.();
    } catch (e: any) {
      const msg = String(e?.message || "");
      if (msg.toLowerCase().includes("billing/payment history")) {
        showToast({ message: "Cannot archive: billing/payment history exists for this patient.", type: "warning" });
      } else {
        showToast({ message: "Failed to archive patient", type: "error" });
      }
    }
  };

  const handleAddVisit = async () => {
    if (!details?.patient_id) return;
    try {
      await apiPost(`/api/visits`, {
        patient_id: details.patient_id,
        visit_date: new Date().toISOString().split('T')[0],
        visit_type: 'Review',
        status: 'Open',
        created_source: 'reception_queue'
      });
      showToast({ message: "Today's visit added", type: "success" });
      onVisitAdded?.();
    } catch (e) {
      showToast({ message: "Failed to add visit", type: "error" });
    }
  };

  const handleSaveEdit = async () => {
    if (!details?.patient_id) return;
    setSavingEdit(true);
    try {
      const updated = await apiPatch(`/api/patients/${details.patient_id}`, editForm) as PatientDetails;
      setDetails({ ...details, ...updated });
      setIsEditing(false);
      showToast({ message: "Patient details updated", type: "success" });
    } catch (e) {
      showToast({ message: "Failed to update patient details.", type: "error" });
    } finally {
      setSavingEdit(false);
    }
  };

  if (!selectedPatient) {
    return (
      <div className="ui-card rc-history-empty">
        <span className="rc-history-empty-icon">🔍</span>
        <p className="rc-history-empty-text">Select a patient to view history</p>
      </div>
    );
  }

  return (
    <div className="ui-card rc-history-card">
      <div className="rc-history-header">
        <h3 className="rc-history-title">Patient Profile</h3>
        <button
          onClick={() => setConfirmArchive(!confirmArchive)}
          title="Archive Patient"
          className="rc-icon-button"
        >
          🗄️
        </button>
      </div>

      {confirmArchive && (
        <div className="rc-history-overlay">
          <div className="rc-history-overlay-icon">⚠️</div>
          <p className="rc-history-overlay-title">Archive this patient record?</p>
          <p className="rc-history-overlay-subtitle">
            This is a soft archive. If this patient has billing/payment history, archive will be blocked for audit safety.
          </p>
          <div className="rc-history-overlay-actions">
            <Button variant="outline" onClick={() => setConfirmArchive(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleArchive}>Archive Patient</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="rc-history-loading">Loading patient records...</div>
      ) : details ? (
        <div className="rc-history-body">
          <div className="rc-history-profile-card">
            {isEditing ? (
              <div className="rc-form">
                <input type="text" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" className="rc-input" />
                <div className="rc-inline-row">
                  <input type="number" value={editForm.age || ""} onChange={(e) => setEditForm({ ...editForm, age: parseInt(e.target.value) || undefined })} placeholder="Age" className="rc-input rc-input-compact" />
                  <select value={editForm.gender || ""} onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })} className="rc-input rc-input-flex">
                    <option value="">Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <input type="text" value={editForm.area || ""} onChange={(e) => setEditForm({ ...editForm, area: e.target.value })} placeholder="Area" className="rc-input" />
                <input type="text" value={editForm.reference_source || ""} onChange={(e) => setEditForm({ ...editForm, reference_source: e.target.value })} placeholder="Reference Source" className="rc-input" />
                <textarea value={editForm.medical_alerts || ""} onChange={(e) => setEditForm({ ...editForm, medical_alerts: e.target.value })} placeholder="Medical Alerts (e.g. Allergy)" className="rc-input rc-textarea" />

                <div className="rc-inline-row rc-inline-row-space">
                  <Button variant="primary" onClick={handleSaveEdit} isLoading={savingEdit} className="rc-flex-1">{savingEdit ? "Saving..." : "Save Changes"}</Button>
                  <Button variant="outline" onClick={() => setIsEditing(false)} disabled={savingEdit} className="rc-flex-1">Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="rc-history-profile-header">
                  <div>
                    <h4 className="rc-history-name">{details.name}</h4>
                    <div className="rc-history-id">#{details.patient_id}</div>
                  </div>
                  <button className="rc-icon-button rc-icon-button-sm" title="Edit Profile" onClick={() => {
                    setIsEditing(true);
                    setEditForm({
                      name: details.name,
                      age: details.age as number,
                      gender: details.gender,
                      area: details.area,
                      reference_source: details.reference_source,
                      medical_alerts: details.medical_alerts
                    });
                  }}>✏️</button>
                </div>
                <div className="rc-history-grid">
                  <div>
                    <div className="rc-history-grid-label">Age/Gender</div>
                    <div className="rc-history-grid-value">{details.age}Y / {details.gender}</div>
                  </div>
                  <div>
                    <div className="rc-history-grid-label">Area</div>
                    <div className="rc-history-grid-value">{details.area || "—"}</div>
                  </div>
                  {details.reference_source && (
                    <div className="rc-history-grid-span">
                      <div className="rc-history-grid-label">Ref Source</div>
                      <div className="rc-history-grid-value">{details.reference_source}</div>
                    </div>
                  )}
                </div>
                {details.medical_alerts && (
                  <div className="rc-alert-box">
                    <div className="rc-alert-title">⚠️ Medical Alerts</div>
                    <p className="rc-alert-text">{details.medical_alerts}</p>
                  </div>
                )}
                <div className="rc-history-actions">
                  <Button variant="outline" onClick={handleAddVisit} fullWidth className="rc-button-outline">
                    ➕ Add Today's Visit Quick
                  </Button>
                </div>
              </>
            )}
          </div>

          <div className="rc-history-tabs">
            <button
              className={`rc-history-tab ${historyTab === 'visits' ? "active" : ""}`}
              onClick={() => setHistoryTab('visits')}
            >
              📅 Visits ({visits.length})
            </button>
            <button
              className={`rc-history-tab ${historyTab === 'followups' ? "active" : ""}`}
              onClick={() => setHistoryTab('followups')}
            >
              📞 Followups ({followups.length})
            </button>
          </div>

          {historyTab === 'visits' ? (
            <div className="rc-history-list">
              {visits.length > 0 ? (
                visits.map((v) => {
                  const d = safeDate(v.visit_date);
                  const isNew = v.visit_type === 'New';
                  return (
                    <div key={v.visit_id} className="rc-history-item-card">
                      <div className="rc-history-item-header">
                        <span className="rc-history-item-date">
                          {d ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : "--"}
                        </span>
                        <span className={`rc-tag ${isNew ? "is-new" : "is-returning"}`}>
                          {v.visit_type}
                        </span>
                      </div>
                      <div>
                        <p className="rc-history-item-notes">{v.remarks || <span className="rc-muted-italic">No clinical notes</span>}</p>
                        {v.complaint && <p className="rc-history-item-complaint"><strong className="rc-history-item-complaint-label">Complaint:</strong> {v.complaint}</p>}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="mod-empty-state rc-empty-block">No visits recorded.</div>
              )}
            </div>
          ) : (
            <div className="rc-history-list">
              {followups.length > 0 ? (
                followups.map((f: any) => {
                  const isCompleted = f.status === 'COMPLETED';
                  return (
                    <div key={f.id} className={`rc-followup-card ${isCompleted ? "is-completed" : ""}`}>
                      <div className="rc-followup-header">
                        <span className="rc-followup-date">Due: {f.scheduled_date}</span>
                        <span className={`rc-tag ${isCompleted ? "is-muted" : "is-warn"}`}>
                          {f.status}
                        </span>
                      </div>
                      <div>
                        <p className="rc-followup-details">{f.procedure_type} - {f.followup_stage}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="mod-empty-state rc-empty-block">No followups scheduled.</div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="rc-history-error">Failed to load patient details.</div>
      )
      }
    </div >
  );
};

export default PatientHistory;
